import { Invoice } from '@/types/transactions'
import { ApiResponse } from '../types'
import { AxiosClient } from '../utils/clients'

export enum MeterType {
    E_CASH_3 = 'E-CASH 3',
    NON_ENERGY = 'nonenergy',
}

export const MMS_METER_TYPES = [
    'MMS-WASION',
    'MMS-CLOU',
    'MMS-MBH',
    'MMS-SECURE',
    'MMS-HEXING',
    'MMS',
    'MMS-CONLOG',
    'MMS-GAMMA',
    'MMS-GEM',
    'MMS-LONGI',
    'MMS-NURI',
    'MMS-HUMANLITECH',
    'MMS-ITRON',
    'CONLOG',
    'MMS-BUTTERFIELD',
    'MMS-BRAINFIELD',
    'MMS-STAR HUYU',
    'MMS-SNEDA',
    'MMS-INEST',
    'MMS-CHINT LUTON ENG',
    'MMS-BUYI EDA CLOVERLAND',
    'MMS-EDA CLOVERLAND',
    'MMS-ITRON-761 SERIES',
    'MMSCLOU',
]

export enum MeterCategory {
    PREPAID = 'prepaid',
    PREPAID_OFFLINE = 'prepaid offline',
    POSTPAID = 'postpaid',
    SLT = 'slt',
    THIRD_PARTY = 'thirdparty',
    NON_SMART = 'Non-smart',
    NON_ENERGY = 'nonenergy',
    VENDOR_DEBT_TRANSACTION = 'VendorDebtPayment',
}

export enum MeterBillingType {
    FLATRATE = 'FLATRATE',
    PREPAID = 'PREPAID',
}

export enum MeterStatus {
    PENDING = 'pending',
    PENDING_REVIEW = 'pending review',
    PENDING_RECONNECTION = 'pending reconnection',
    DISCONNECTED = 'disconnected',
}

export type Meter = {
    id: string
    meterNumber: string
    accountNumber: string
    balance: string | null
    meterType: MeterType | null
    meterCategory?: MeterCategory
    alias: string
    nameOnMeter: string
    consumptionAnomalyProfile: string
    address: string
    spn: string
    ghanaPostAddress: string
    location?: {
        centerLatitude?: string
        centerLongitude?: string
        northLat?: string
        northLong?: string
        southLat?: string
        southLong?: string
        eastLat?: string
        eastLong?: string
        westLat?: string
        westLong?: string
        gpsName?: string
        postCode?: string
        district?: string
        region?: string
        area?: string
        street?: string
    }
    outstandingBalance?: string | null
    createdBy: string
    createdAt: string
    updatedAt: string | null
    region: string | null
    district: string | null
    regionId: string
    districtId: string
    fraudStatus: boolean
    billingType: MeterBillingType | null
    meterStatus: string | MeterStatus | null
    lowBalanceAlertLimit: number
    lowBalanceAlertLimitUpdatedAt: Date | null
    lowBalanceAlertEnabled: boolean
    replacementReceivedAt?: Date | null
    replacementCompletedAt?: Date | null
    replacementStatus?: 'pending' | 'completed' | 'underReview' // Limited to specific statuses
    replacementInitiatedAt?: Date | null
    isCreditAmountDisputed: boolean // Indicates a wrong balance detection
    creditTransferStatus?: 'pending' | 'completed' | 'underReview' // Limited to specific statuses
    isReplacementMeter: boolean // Indicates if the meter is replaced
    meterReplacedAt?: Date | null
    cardType: string

    hasDebt: boolean // Indicates if there is any debt to pay
    debtAmount?: number | null
    debtAmountUpdatedAt?: Date | null
    debtAmountPaid?: boolean | null
    hasCredit: boolean // Indicates if there is any credit to transfer
    creditAmount?: number | null
    creditAmountUpdatedAt?: Date | null
    creditAmountTransferred?: boolean | null
    debtDueDate?: Date | null
    trackingCode?: string | null

    balanceDescription: string
    consumptionKwh: number
    lastUpdatedAt: string
    balanceIsAccurate: boolean

    previousMeterNumber?: string | null
    previousMeterType?: string | null
    previousMeterCategory?: string | null

    isNewIyInstalled?: boolean | null
    thresholdAmount?: number | null
    thresholdMultiplier?: number | null
    firstTopupCompleted: boolean | null
    meterPhase: string | null
    replacedAt: string | null
}

export type Device = Meter | ThirdPartyVendor

export type DevicesFilter = {
    Page: number
    PageSize: number
    SortColumn: string
    SortDir: string
    Id: string
    Alias: string
    MeterNumber: string
    AccountNumber: string
    StartCreatedAt: string
    EndCreatedAt: string
}

export type GetThirdPartyVendorsResponse = ApiResponse<{
    indexFrom: number
    pageIndex: number
    pageSize: number
    totalCount: number
    totalPages: number
    results: ThirdPartyVendor[]
    hasPreviousPage: true
    hasNextPage: true
}>

export type ThirdPartyVendorSupplier = {
    name: string
    tag: string
}
export type ThirdPartyVendor = {
    id: string
    vendorId: string
    vendorName: string
    phoneNumber: string
    meterType: string
    meterCategory?: MeterCategory
    nameOnMeter?: string
    accountNumber: string
    companyName: string
    status: string
    expiry: string
    balance: string
    otherPhoneNumbers: string[]
    createdAt: string
    updatedAt: string
    outstandingBalance?: string
    region?: string | null
    district?: string | null
    address?: string
    spn?: string
    fraudStatus: boolean
    meterNumber?: string
    consumptionAnomalyProfile: string
    billingType: string
}
export type AddThirdPartyVendorResponse = ApiResponse<ThirdPartyVendor>

export type AllDevicesResponse = ApiResponse<{
    results: Device[]
    lowerBound: number
    upperBound: number
    pageIndex: number
    totalPages: number
    pageSize: number
    totalCount: number
}>

export type SingleDeviceResponse = ApiResponse<Device>

export type AddMeterPayload = {
    ghanaPostAddress: string
    meterCategory: MeterCategory
    meterNumber: string
    accountNumber: string
    alias: string
}

export type MeterBrandsResponse = ApiResponse<
    {
        name: string
        online: boolean
    }[]
>

export type VerifyGhanaPostGPSLocationResponse = {
    district: string
    region: string
    area: string
    street: string
    gpsName: string
}

export type GhanaPostCodeFromRawGPSResponse = {
    district: string
    region: string
    area: string
    street: string
    gpsName: string
}

export type VerifyDeviceParams = { MeterNumber?: string; MeterCategory: string; AccountNumber?: string }

export type VerifyMeterDetails = {
    meterNumber: string
    accountNumber: string
    balance: null | string
    spn: string
    customerName: string
    address: string
    meteringSystem: string
    unsupported: boolean
    regionId: string
    districtId: string
    cardType: string
    isPrepaidOffline: boolean
    region: string
    district: string
    nameOnMeter: string
    meterType: string
    meterStatus: string
    outstandingBalance: null | string
    createdBy: null | string
    polymorphEcgDevices: null | string
}

export type ThirdPartyQuota = {
    amount: null | string
    walletId: string
}

export type MetersSummaryTabs = 'meter-details' | 'transactions'

export type VerifyDeviceResponse = ApiResponse<VerifyMeterDetails>
export type VerifyThirdPartyVendorResponse = ApiResponse<ThirdPartyVendor>

export type TopUpThirdPartyResponse = ApiResponse<ThirdPartyQuota>
export type DeviceDistrict = { name: string; value: string }
export type DeviceRegion = { name: string; value: string }

export type PrepaidDeviceNotFoundReportPayload = {
    meterNumber: string
    accountNumber: string
    region: string
    district: string
    ghanaPostAddress: string
    latitude: string
    longitude: string
    alias: string
    meterCategory: MeterCategory
    source: string
}

// Generated by https://quicktype.io

export interface GetAllDeviceAccountsResponse {
    meters: Meter[]
    slt: Device[]
    inStoreVendors: ThirdPartyVendor[] | null
}

type NextPurchasePredictionResponse = ApiResponse<{
    currentAmount: number
    predictedDaysTo12: number
    predictedPurchaseDate: string
    purchaseDate: string
}>

type LowBalanceLimitResponse = ApiResponse<{
    lowBalanceAlertLimit: number
    lowBalanceAlertLimitUpdatedAt: Date
    lowBalanceAlertEnabled: boolean
}>

export type LowBalanceLimitRequest = {
    amount: number
    meterId: string
}

export type UpdateLowBalanceLimitRequest = {
    toggle: boolean
    action: 'toggle' | 'updateLimit'
    amount?: number
    meterId: string
}

export type DeviceDebtStatusResponse = ApiResponse<{
    dueDebtAmount: number
    totalDebtAmount: number
    customerName: string
    debtDescription: string
    totalDebtAmountAvailable: boolean
    dueDebtAmountAvailable: boolean
}>

export type UpdatePreviousMeterRequest = {
    alias: string
    newMeterNumber: string
    oldMeterId: string
    latitude: string
    longitude: string
}

export interface DevicePreviousBalanceResponse {
    id: string
    code: string
    type: string
    accountNumber: string
    meterNumber: string
    servicePointNumber: string
    amount: number
    status: string
}

export interface PrepaidMeterBalance {
    balance: number | string | null
    balanceDescription: string | null
    consumptionKwh?: number | null
    lastUpdatedAt?: string | null
    balanceIsAccurate?: boolean
}

export interface PostpaidDebt {
    meterId: string
    accountNumber: string
    openingBalance: number
    consumptionBalance: number
    paymentSum: number
    closingBalance: number
    consumptionKWh: number
    billDate?: string // ISO date format
    dueDate?: string // ISO date format
    billMonth: string
    adjustment: number
    meterAlias: string
}

export default class DeviceRepository {
    private BASE_URL = 'https://api-ecg-devices.gov-gh.com'
    // private BASE_URL = 'http://eager-candies-lie.loca.lt'
    private BASE_VENDOR_URL = 'https://api-ecg-vendors.gov-gh.com'

    constructor(private client: AxiosClient) {}

    async getAllDevices(filters: Partial<DevicesFilter>) {
        const res = await this.client.get<AllDevicesResponse>(`${this.BASE_URL}/devices`, {
            params: filters,
        })
        return res.data
    }
    async getAllDeviceAccounts() {
        const res = await this.client.get<ApiResponse<GetAllDeviceAccountsResponse>>(
            `${this.BASE_URL}/devices/accounts`,
        )
        return res.data
    }

    async getAllDevicesWithToken(token: string) {
        const res = await this.client.get<ApiResponse<Device[]>>(`${this.BASE_URL}/devices/token/${token}`)
        return res.data
    }

    async getSingleDevices(deviceId: string) {
        const res = await this.client.get<SingleDeviceResponse>(`${this.BASE_URL}/devices/${deviceId}`)
        return res.data
    }

    async getDeviceBalance(deviceId: string) {
        const res = await this.client.get<SingleDeviceResponse>(`${this.BASE_URL}/devices/${deviceId}/balance`)
        return res.data
    }

    async updateDevice(deviceId: string, payload: Partial<Device>) {
        const res = await this.client.patch<SingleDeviceResponse>(`${this.BASE_URL}/devices/${deviceId}`, payload)
        return res.data
    }

    async deleteDevice(deviceId: string) {
        const res = await this.client.delete<SingleDeviceResponse>(`${this.BASE_URL}/devices/${deviceId}`)
        return res.data
    }

    async addDevice(payload: AddMeterPayload) {
        const res = await this.client.post<SingleDeviceResponse>(`${this.BASE_URL}/devices/`, payload)
        return res.data
    }

    async updatePreviousMeter(payload: UpdatePreviousMeterRequest) {
        const res = await this.client.patch<ApiResponse<any>>(`${this.BASE_URL}/devices/update-previous-meter`, payload)
        return res.data
    }

    async getMeterBrands() {
        const res = await this.client.get<MeterBrandsResponse>(`${this.BASE_URL}/devices/meter-brands`)
        return res.data
    }
    async verifyGhanaPostGPSLocation(ghanaPostGPSAddress: string) {
        const res = await this.client.get<VerifyGhanaPostGPSLocationResponse>(
            `${this.BASE_URL}/devices/verify-location/${ghanaPostGPSAddress}`,
        )
        return res.data
    }

    async getGhanaPostCodeFromRawGPS(lat: string, long: string) {
        const res = await this.client.get<ApiResponse<GhanaPostCodeFromRawGPSResponse>>(
            `${this.BASE_URL}/devices/transform-location?Latitude=${lat}&Longitude=${long}`,
        )
        return res.data
    }

    async verifyDevice(params: VerifyDeviceParams) {
        const res = await this.client.get<VerifyDeviceResponse>(`${this.BASE_URL}/devices/verify-device`, {
            params,
        })
        return res.data
    }

    async getThirdPartyVendor() {
        const res = await this.client.get<GetThirdPartyVendorsResponse>(`${this.BASE_VENDOR_URL}/vendors`)
        return res.data
    }

    async addThirdPartyVendor(payload: { meterType: string; userId: string }) {
        const res = await this.client.post<AddThirdPartyVendorResponse>(`${this.BASE_VENDOR_URL}/vendors`, payload)
        return res.data
    }

    async verifyThirdPartyVendorId(params: { meterType: string; userId: string }) {
        const res = await this.client.get<VerifyThirdPartyVendorResponse>(`${this.BASE_VENDOR_URL}/vendors/verify`, {
            params,
        })
        return res.data
    }

    async getThirdPartyMeterDetails(id: string | number) {
        const res = await this.client.get<ThirdPartyVendor>(`${this.BASE_VENDOR_URL}/vendors/${id}`)
        return res.data
    }

    async getThirdPartyMeterSuppliers() {
        const res = await this.client.get<ApiResponse<ThirdPartyVendorSupplier[]>>(
            `${this.BASE_VENDOR_URL}/vendors/meter-suppliers`,
        )
        return res.data
    }

    async deleteThirdPartyMeter(id: string) {
        const res = await this.client.delete<ApiResponse<null>>(`${this.BASE_VENDOR_URL}/vendors/${id}`)
        return res.data
    }

    async deviceRegions() {
        const res = await this.client.get<ApiResponse<DeviceRegion[]>>(`${this.BASE_URL}/devices/region-list/`)
        return res.data
    }

    async deviceDistricts(code: string) {
        const res = await this.client.get<ApiResponse<DeviceDistrict[]>>(
            `${this.BASE_URL}/devices/district-list/${code}`,
        )
        return res.data
    }

    async createPrepaidDeviceNotFoundReport(payload: PrepaidDeviceNotFoundReportPayload) {
        // https://api-ecg-devices.gov-gh.com/devices/not-found
        const res = await this.client.post<ApiResponse<Device>>(`${this.BASE_URL}/devices/not-found`, payload)
        return res.data
    }

    async addServiceRequestAsMeter(payload: { accountNumber: string }) {
        const res = await this.client.put<ApiResponse<SingleDeviceResponse>>(
            `${this.BASE_URL}/devices/request-service/${payload.accountNumber}/meter`,
        )

        return res
    }

    async getNextPurchasePrediction(accountNumber: string) {
        const res = await this.client.get<NextPurchasePredictionResponse>(
            `${this.BASE_URL}/devices/next-predicted-purchase/${accountNumber}`,
        )

        return res.data
    }

    async getDeviceLimitCheck() {
        const res = await this.client.get<
            ApiResponse<{
                isLimitReached: boolean
                deviceCount: number
            }>
        >(`${this.BASE_URL}/devices/customer-device-limit-check`)
        return res.data
    }

    async addLowBalanceLimit(payload: LowBalanceLimitRequest) {
        const res = await this.client.post<LowBalanceLimitResponse>(
            `${this.BASE_URL}/devices/low-balance-limit`,
            payload,
        )
        return res.data
    }

    async updateLowBalanceLimit(payload: UpdateLowBalanceLimitRequest) {
        const res = await this.client.patch<LowBalanceLimitResponse>(
            `${this.BASE_URL}/devices/low-balance-limit`,
            payload,
        )
        return res.data
    }

    async getDeviceDebtStatus(meterId: string) {
        const res = await this.client.get<DeviceDebtStatusResponse>(
            `${this.BASE_URL}/devices/${meterId}/debt-balance-status`,
        )
        return res.data
    }

    async getPreviousBalance(meterId: string) {
        const res = await this.client.get<ApiResponse<DevicePreviousBalanceResponse>>(
            `${this.BASE_URL}/devices/previous-meter-balance/${meterId}`,
        )
        return res.data
    }

    async getPrepaidDeviceMlBalance(deviceId: string) {
        const res = await this.client.get<ApiResponse<PrepaidMeterBalance>>(
            `${this.BASE_URL}/devices/ml-meter-balance/${deviceId}`,
            {
                useCache: true,
            },
        )
        return res.data
    }

    async updateMeterInstallationState({ deviceId, transactionId }: { deviceId: string; transactionId: string }) {
        const res = await this.client.patch<ApiResponse<Device>>(
            `${this.BASE_URL}/devices/meter-installation-state/${deviceId}/${transactionId}`,
            {
                transactionId,
            },
        )
        return res.data
    }

    async getPostpaidDebt() {
        const res = await this.client.get<ApiResponse<PostpaidDebt[]>>(`${this.BASE_URL}/devices/postpaid-debt`)
        return res.data
    }

    async getVendorDebts() {
        const res = await this.client.get<ApiResponse<Invoice[]>>(`${this.BASE_URL}/devices/vendor-debts`)
        return res.data
    }
}
